import React, { useRef, useState, useEffect, forwardRef } from "react";
import { Box, Typography, Button, Paper } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { addTermsAcceptanceLog, fetchTermsHtml } from "@/api";
import { useAuth, useUser } from "@clerk/clerk-react";
import { getClientMetadata } from "@/utils/clientMetaData";
import { useAppSelector } from "@/state/hooks";
import { Spinner } from "@fluentui/react";
import DOMPurify from "isomorphic-dompurify";
import { trackConversion } from "@/utils/analytics";

interface TermsAndConditionsProps {
    onAccept: () => void;
    tcVersion?: string; // optional, for display/telemetry
}

const TermsAndConditions: React.FC<TermsAndConditionsProps> = ({ onAccept, tcVersion }) => {
    const scrollRef = useRef<HTMLDivElement>(null);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const { getToken } = useAuth();
    const { user } = useUser();
    const userId = useAppSelector(s => s.userProfiles.userProfile?.userid);

    const [isSaving, setIsSaving] = useState(false);
    const [isLoadingTerms, setIsLoadingTerms] = useState(true); // ⬅️ new: only tracks HTML load
    const [termsHtml, setTermsHtml] = useState<string>("");
    const [error, setError] = useState<string | null>(null);
    const mountTimeRef = useRef<number | null>(null);

    // Fetch Terms HTML (served from DB by /api/terms)
    useEffect(() => {
        (async () => {
            try {
                const rawHtml = await fetchTermsHtml();
                const parser = new DOMParser();
                const doc = parser.parseFromString(rawHtml, "text/html");
                const main = doc.querySelector("main, article, .terms, [role='main'], .w-richtext") || doc.body;

                normalizeBoldToStrong(doc, main);
                promoteStrongTitleToH1(doc, main);
                fixHeadingHierarchy(doc, main);

                const clean = DOMPurify.sanitize(main.innerHTML, {
                    ADD_ATTR: ["target", "rel"],
                    FORBID_ATTR: ["style"]
                });
                setTermsHtml(clean);
            } catch (e: any) {
                setError(e?.message || "Unable to load terms");
                setTermsHtml("");
            } finally {
                setIsLoadingTerms(false);
            }
        })();
    }, []);

    // Scroll tracking
    useEffect(() => {
        mountTimeRef.current = Date.now();
        const el = scrollRef.current;
        if (!el) return;
        const onScroll = () => {
            const isBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 10;
            if (isBottom) setHasScrolledToBottom(true);
        };
        el.addEventListener("scroll", onScroll);
        return () => el.removeEventListener("scroll", onScroll);
    }, []);

    function normalizeBoldToStrong(doc: Document, main: Element) {
        Array.from(main.querySelectorAll("b")).forEach(b => {
            const strong = doc.createElement("strong");
            strong.innerHTML = b.innerHTML;
            b.replaceWith(strong);
        });

        const boldClasses = new Set<string>();
        Array.from(doc.querySelectorAll("style")).forEach(styleEl => {
            const css = styleEl.textContent || "";
            const re = /\.([A-Za-z0-9_-]+)\s*\{[^}]*font-weight\s*:\s*(bold|[6-9]00)\b/gi;
            for (const m of css.matchAll(re)) boldClasses.add(m[1]);
        });

        if (boldClasses.size) {
            Array.from(main.querySelectorAll<HTMLElement>("[class]")).forEach(el => {
                const classes = (el.getAttribute("class") || "").split(/\s+/);
                if (classes.some(c => boldClasses.has(c))) {
                    const strong = doc.createElement("strong");
                    strong.innerHTML = el.innerHTML;
                    el.replaceWith(strong);
                }
            });
        }

        Array.from(main.querySelectorAll<HTMLElement>("[style*='font-weight']")).forEach(el => {
            const style = (el.getAttribute("style") || "").toLowerCase();
            const m = style.match(/\bfont-weight\s*:\s*([^;]+)/);
            const weight = m ? m[1].trim() : "";
            if (weight === "bold" || (/^\d+$/.test(weight) && parseInt(weight, 10) >= 600)) {
                const strong = doc.createElement("strong");
                strong.innerHTML = el.innerHTML;
                el.replaceWith(strong);
            }
        });
    }

    function promoteStrongTitleToH1(doc: Document, main: Element) {
        const blocks = Array.from(main.children) as HTMLElement[];
        const isStrongOnlyP = (el: HTMLElement) =>
            el.tagName === "P" && el.children.length === 1 && el.firstElementChild?.tagName === "STRONG" && (el.textContent || "").trim().length > 0;

        let candidate: HTMLElement | null = null;
        for (const el of blocks) {
            if ((el.textContent || "").trim().length === 0) continue;
            if (isStrongOnlyP(el)) {
                candidate = el;
                break;
            }
            if (el.tagName === "DIV") {
                const firstChild = el.firstElementChild as HTMLElement | null;
                if (firstChild && isStrongOnlyP(firstChild)) {
                    candidate = firstChild;
                    break;
                }
            }
            break;
        }
        if (candidate) {
            const h1 = doc.createElement("h1");
            h1.innerHTML = (candidate.firstElementChild as HTMLElement).innerHTML;
            candidate.replaceWith(h1);
        }
    }

    function fixHeadingHierarchy(doc: Document, main: Element) {
        const blocks = Array.from(main.children) as HTMLElement[];
        const findTitlePara = () => {
            for (const el of blocks) {
                if ((el.textContent || "").trim().length === 0) continue;
                const tryEl = (cand: HTMLElement | null) => {
                    if (!cand || cand.tagName !== "P") return null;
                    const txt = (cand.textContent || "").trim();
                    if (/^terms and conditions$/i.test(txt)) return cand;
                    return null;
                };
                let p: HTMLElement | null = null;
                if ((p = tryEl(el))) return p;
                if (el.tagName === "DIV") {
                    p = tryEl(el.firstElementChild as HTMLElement | null);
                    if (p) return p;
                }
                break;
            }
            return null;
        };

        const titleP = findTitlePara();
        if (titleP) {
            const h1 = doc.createElement("h1");
            h1.textContent = (titleP.textContent || "").trim();
            titleP.replaceWith(h1);
        }

        const h1s = Array.from(main.querySelectorAll("h1"));
        if (h1s.length > 1) {
            h1s.slice(1).forEach(h1 => {
                const h2 = doc.createElement("h2");
                h2.innerHTML = h1.innerHTML;
                h1.replaceWith(h2);
            });
        }
    }

    const handleAccept = async () => {
        // Track button click event
        trackConversion.termsButtonClicked(tcVersion);

        setIsSaving(true);
        try {
            const idToken = await getToken({ template: "Intelligenic" });
            if (!idToken || !userId) throw new Error("Missing ID token or userId");

            const payload = await getClientMetadata();
            const timeSpent = mountTimeRef.current ? Math.round((Date.now() - mountTimeRef.current) / 1000) : null;

            // Extract attribution data from Clerk metadata
            // Try unsafeMetadata first, fallback to publicMetadata
            const unsafeMetadata = user?.unsafeMetadata || {};
            const publicMetadata = user?.publicMetadata || {};

            const clerkAttribution = {
                gclid: (unsafeMetadata.gclid || publicMetadata.gclid) as string | null,
                utm_source: (unsafeMetadata.utm_source || publicMetadata.utm_source) as string | null,
                utm_medium: (unsafeMetadata.utm_medium || publicMetadata.utm_medium) as string | null,
                utm_campaign: (unsafeMetadata.utm_campaign || publicMetadata.utm_campaign) as string | null
            };

            // Log attribution data sources for debugging
            // console.log('📊 T&C Attribution Debug:', {
            //     fromPayload: {
            //         gclid: payload.gclid,
            //         utm_source: payload.utm_source
            //     },
            //     fromClerk: {
            //         gclid: clerkAttribution.gclid,
            //         utm_source: clerkAttribution.utm_source
            //     },
            //     clerkMetadata: {
            //         unsafeMetadata,
            //         publicMetadata
            //     }
            // });

            // Merge Clerk attribution as fallback (only for missing values in payload)
            // This ensures gclid persists even if user navigated away from landing page
            const enrichedPayload = {
                ...payload,
                gclid: payload.gclid || clerkAttribution.gclid,
                utm_source: payload.utm_source || clerkAttribution.utm_source,
                utm_medium: payload.utm_medium || clerkAttribution.utm_medium,
                utm_campaign: payload.utm_campaign || clerkAttribution.utm_campaign,
                timeSpentOnTermsPageSeconds: timeSpent,
                tc_version: tcVersion ?? null // for analytics; backend still derives active agreement
            };

            // Save acceptance to backend (also triggers backend GA4 Measurement Protocol event)
            await addTermsAcceptanceLog(enrichedPayload, idToken, userId);

            // Track to GA4 via gtag for session continuity and real-time reporting
            // Include attribution data from Clerk metadata for explicit parameter tracking
            trackConversion.termsAccepted(tcVersion, timeSpent || undefined, clerkAttribution);

            onAccept();
        } catch (err) {
            console.error("Terms log error:", err);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Box sx={{ backgroundColor: "#f4f4f7", width: "calc(100vw - 400px)", margin: "0 auto", mt: 4, p: 2, textAlign: "center" }}>
            <Typography variant="h5" fontWeight="bold">
                Terms and Conditions
            </Typography>
            <Typography variant="subtitle1" mt={1} mb={2}>
                Please read and scroll through the complete terms to continue
            </Typography>

            <Paper
                ref={scrollRef}
                elevation={1}
                sx={{
                    height: "calc(100vh - 400px)",
                    overflowY: "scroll",
                    p: 2,
                    textAlign: "left",
                    mb: 2,
                    border: "1px solid #ccc"
                }}
            >
                {isLoadingTerms ? (
                    <Box display="flex" alignItems="center" justifyContent="center" sx={{ height: "30vh" }}>
                        <Spinner />
                    </Box>
                ) : error ? (
                    <Typography color="error" variant="body2">
                        {error}
                    </Typography>
                ) : (
                    <Box
                        className="tc-content"
                        sx={{
                            "& p": { m: 0, mb: "0.75rem", lineHeight: 1.6 },
                            "& .w-richtext > div": { mb: "0.75rem", lineHeight: 1.6 },
                            "& ul": {
                                pl: "1.5rem",
                                m: "0 0 0.75rem",
                                listStyleType: "disc",
                                listStylePosition: "outside"
                            },
                            "& ol": {
                                pl: "1.5rem",
                                m: "0 0 0.75rem",
                                listStyleType: "decimal",
                                listStylePosition: "outside"
                            },
                            "& li": { mb: "0.25rem" },
                            "& a": { textDecoration: "underline", "&:hover": { color: "primary.dark" } },
                            "&& strong, && b": { fontWeight: 700 },
                            "& h1": {
                                fontSize: "2rem",
                                lineHeight: 1.2,
                                fontWeight: 700,
                                mt: 2,
                                mb: 1.5,
                                color: "#103699"
                            },
                            "& h2": {
                                fontSize: "1.25rem",
                                fontWeight: 700,
                                mt: 2,
                                mb: 1,
                                position: "relative",
                                color: "#103699"
                            },
                            "& h3": {
                                fontSize: "1.1rem",
                                fontWeight: 700,
                                mt: 1.5,
                                mb: 0.75,
                                position: "relative",
                                color: "#103699"
                            },
                            counterReset: "none",
                            "& h2::before, & h3::before": { content: '""', counterIncrement: "none", marginRight: 0 }
                        }}
                        dangerouslySetInnerHTML={{ __html: termsHtml }}
                    />
                )}
            </Paper>

            {!hasScrolledToBottom ? (
                <Box sx={{ backgroundColor: "#e3f2fd", color: "#1976d2", p: 1, borderRadius: 1, mb: 2 }}>
                    <Typography variant="body2">Please scroll to the bottom to read all terms before continuing</Typography>
                </Box>
            ) : (
                <Box
                    sx={{
                        color: "#2e7d32",
                        p: 1,
                        borderRadius: 1,
                        mb: 2,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 1
                    }}
                >
                    <CheckCircleIcon color="success" />
                    <Typography variant="body2">You have read the complete terms</Typography>
                </Box>
            )}

            {isSaving ? (
                <Box display="flex" justifyContent="center" alignItems="center" height={48}>
                    <Spinner />
                </Box>
            ) : (
                <Button
                    variant="contained"
                    color="primary"
                    disabled={!hasScrolledToBottom}
                    fullWidth
                    onClick={handleAccept}
                    sx={{ height: 48, fontWeight: "bold" }}
                >
                    I Accept Terms and Conditions
                </Button>
            )}
        </Box>
    );
};

export default TermsAndConditions;
